import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { cleanLabel } from "@/lib/mentions";
import { Input } from "@/components/ui/input";
import { Building2, Package, Loader2 } from "lucide-react";

// Mesaj kutusu: `@` → cari, `#` → ürün canlı öneri listesi (ok tuşları + Enter/Tab ile seçim)
export default function MentionInput({ value, onChange, onPick, onSend, onPaste, placeholder, testid = "chat-input" }) {
  const ref = useRef(null);
  const [trig, setTrig] = useState(null);   // {sigil, start, query}
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);

  const detect = (text, caret) => {
    const m = text.slice(0, caret).match(/(^|\s)([@#])([^\s@#]*)$/);
    return m ? { sigil: m[2], start: caret - m[3].length - 1, query: m[3] } : null;
  };
  const update = (e) => {
    onChange(e.target.value);
    setTrig(detect(e.target.value, e.target.selectionStart ?? e.target.value.length));
  };

  useEffect(() => {
    if (!trig) { setItems([]); return undefined; }
    let alive = true;
    setBusy(true);
    const t = setTimeout(() => {
      api.get("/chat/mentions", { params: { type: trig.sigil === "@" ? "contact" : "product", q: trig.query } })
        .then((r) => { if (alive) { setItems(r.data || []); setIdx(0); } })
        .catch(() => { if (alive) setItems([]); })
        .finally(() => { if (alive) setBusy(false); });
    }, 180);
    return () => { alive = false; clearTimeout(t); };
  }, [trig?.sigil, trig?.query]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (it) => {
    if (!it || !trig) return;
    const label = cleanLabel(it.label);
    const before = value.slice(0, trig.start);
    const after = value.slice(trig.start + 1 + trig.query.length);
    onChange(`${before}${trig.sigil}${label} ${after}`);
    onPick({ type: it.type, id: it.id, label });
    setTrig(null);
    requestAnimationFrame(() => {
      const el = ref.current; if (!el) return;
      const pos = before.length + label.length + 2;
      el.focus(); el.setSelectionRange(pos, pos);
    });
  };
  const onKey = (e) => {
    if (trig && items.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => (i + 1) % items.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => (i - 1 + items.length) % items.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(items[idx]); return; }
    }
    if (e.key === "Escape" && trig) { setTrig(null); return; }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); }
  };

  return (
    <div className="relative flex-1 min-w-0">
      {trig && (
        <div className="absolute bottom-full mb-2 left-0 w-full sm:w-[440px] rounded-xl border bg-popover shadow-xl overflow-hidden z-30" data-testid="chat-mention-list">
          <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b flex items-center gap-1.5">
            {trig.sigil === "@" ? <><Building2 className="h-3 w-3" />Cari etiketle</> : <><Package className="h-3 w-3" />Ürün etiketle</>}
            <span className="font-normal normal-case tracking-normal">· "{trig.query || "…"}"</span>
            {busy && <Loader2 className="h-3 w-3 animate-spin ml-auto" />}
          </div>
          <div className="max-h-64 overflow-y-auto overscroll-contain" onWheel={(e) => e.stopPropagation()}>
            {items.length === 0 && !busy && <div className="px-3 py-2.5 text-xs text-muted-foreground" data-testid="chat-mention-empty">Sonuç yok — yazmaya devam edin</div>}
            {items.map((it, i) => (
              <button key={it.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(it); }} onMouseEnter={() => setIdx(i)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${i === idx ? "bg-primary/10" : "hover:bg-muted/60"}`} data-testid={`chat-mention-${it.id}`}>
                <span className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${it.type === "contact" ? "bg-secondary/10 text-secondary" : "bg-emerald-50 text-emerald-700"}`}>
                  {it.type === "contact" ? <Building2 className="h-3.5 w-3.5" /> : <Package className="h-3.5 w-3.5" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold truncate">{it.label}</span>
                  {it.sub && <span className="block text-[11px] text-muted-foreground truncate">{it.sub}</span>}
                </span>
                {it.type === "product" && <span className={`text-[11px] font-bold tabular-nums shrink-0 ${Number(it.qty) > 0 ? "text-emerald-700" : "text-red-600"}`}>{it.qty} {it.unit}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      <Input ref={ref} value={value} onChange={update} onKeyDown={onKey} onPaste={onPaste} placeholder={placeholder} className="h-10" autoComplete="off" data-testid={testid} />
    </div>
  );
}
